import _ from 'lodash'
import * as React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { CardBody, CardText, Card } from 'reactstrap'

import { updateTargetValue, selectPlansStatus } from 'slices/plansSlice'

import { CustomModal, InputGroupFormat } from 'components/common'
import * as Rules from 'components/common/FormFormat/ValidationRules'

import usePlans from 'hooks/usePlans'

import styles from './TargetValuesUpdate.module.scss'

type TargetValuesType = {
  scheduleTypeId: number
  targetValue: string | undefined
  name: string
  unit: string
}

type Props = {
  isOpen: boolean
  workspaceId: number
  workspaceName: string
  isPast: boolean
  onSuccess: () => void
  onCancel: () => void
}

const TargetValuesUpdate: React.FC<Props> = ({ isOpen, workspaceId, workspaceName, isPast, onSuccess, onCancel }) => {
  const [updateValues, setUpdateValues] = React.useState<TargetValuesType[]>([])
  const [initUpdateValues, setInitUpdateValues] = React.useState<TargetValuesType[]>([])
  const [validities, setValidities] = React.useState<boolean[]>([])
  const [disabled, setDisabled] = React.useState(true)
  const [modalErrorMessage, setModalErrorMessage] = React.useState<string | undefined>(undefined)
  const [submitted, setSubmitted] = React.useState(false)

  const { isRequesting, errorMessage } = useSelector(selectPlansStatus, shallowEqual)
  const dispatch = useDispatch()

  const { planWorkDate, planScheduleTypes, dailyTarget } = usePlans()

  React.useEffect(() => {
    if (isRequesting || !submitted) {
      return
    }
    if (errorMessage === '') {
      onSuccess()
    } else {
      setModalErrorMessage('保存できませんでした。')
    }
    setSubmitted(false)
  }, [errorMessage, isRequesting, onSuccess, submitted])

  React.useEffect(() => {
    const nextDisabled = !(updateValues.every(t => t.targetValue) && validities.every(validity => validity))
    setDisabled(nextDisabled)
  }, [updateValues, validities])

  React.useEffect(() => {
    const newTargetValues = dailyTarget?.data.reduce((acc: TargetValuesType[], cur) => {
      const scheduleType = planScheduleTypes.filter(s => s.dataConnection).find(s => s.id === cur.scheduleTypeId)
      if (scheduleType) {
        acc.push({
          scheduleTypeId: cur.scheduleTypeId,
          targetValue: cur.target?.toString() || '',
          name: scheduleType.name,
          unit: scheduleType.unit!,
        })
      }
      return acc
    }, [])
    const sortedTargetValues = _.sortBy(newTargetValues, 'name')
    setUpdateValues(sortedTargetValues)
    setInitUpdateValues(sortedTargetValues)
    setValidities(sortedTargetValues.map(() => true))
    setModalErrorMessage(undefined)
  }, [isOpen, planScheduleTypes, dailyTarget])

  const handleUpdateValue = (index: number, targetValue: string) => {
    const newTargetValues = updateValues.map((target, i) => (i === index ? { ...target, targetValue } : target))
    setUpdateValues(newTargetValues)
  }

  const handleValidate = (index: number, validity: boolean) => {
    if (validities[index] !== validity) {
      setValidities(validities.map((v, i) => (i === index ? validity : v)))
    }
  }

  const handleSaveClick = () => {
    setSubmitted(true)
    const dailyTargets = updateValues
      .filter((value, index) => value.targetValue !== initUpdateValues[index].targetValue)
      .map(t => ({
        workDate: planWorkDate,
        scheduleTypeId: t.scheduleTypeId,
        value: Number(t.targetValue),
      }))
    dispatch(updateTargetValue(workspaceId, { dailyTargets }))
  }

  const unchanged = React.useMemo(() => _.isEqual(initUpdateValues, updateValues), [initUpdateValues, updateValues])

  return (
    <CustomModal
      isOpen={isOpen}
      title="目標設定"
      approveDisabled={disabled || unchanged}
      showApprove={!isPast}
      errorMessage={modalErrorMessage}
      onCancel={onCancel}
      onApprove={handleSaveClick}
      onHideNotification={() => setModalErrorMessage(undefined)}
    >
      <p>この日の各作業の目標を設定します。</p>
      <Card className={styles.cardContainer}>
        <CardBody>
          <CardText>{workspaceName}</CardText>
          {updateValues.map((target, index) => (
            <InputGroupFormat
              key={target.scheduleTypeId}
              label={target.name}
              value={target.targetValue}
              addonText={target.unit}
              maxLength={7}
              disabled={isPast}
              onChange={value => handleUpdateValue(index, value)}
              validations={[Rules.Required, Rules.PositiveInteger]}
              onValidate={validity => handleValidate(index, validity)}
              className={index === updateValues.length - 1 ? '' : 'mb-3'}
            />
          ))}
        </CardBody>
      </Card>
    </CustomModal>
  )
}

export default TargetValuesUpdate
